'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import React, { useCallback, useMemo } from 'react';

import { StepIndicator } from './step-indicator';
import { trackEvent } from '@/lib/event-tracking';
import {
  EATING_GOALS,
  type EatingGoal,
  useAIPlannerStore,
} from '@/store/ai-planner';

export function StepEatingGoals() {
  const currentStep = useAIPlannerStore.use.currentStep();
  const preferences = useAIPlannerStore.use.preferences();
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const nextStep = useAIPlannerStore.use.nextStep();
  const prevStep = useAIPlannerStore.use.prevStep();

  const selectedGoals = useMemo(() => {
    return preferences.eatingGoals ?? [];
  }, [preferences.eatingGoals]);

  const handleGoalToggle = useCallback(
    (goal: EatingGoal) => {
      // Single selection: if clicking same goal, deselect it; otherwise select the new one
      const newGoals = selectedGoals.includes(goal) ? [] : [goal];
      setPreferences({ eatingGoals: newGoals });
    },
    [selectedGoals, setPreferences]
  );

  return (
    <div className="p-0">
      {/* Background Image */}
      <div className="absolute -top-40 inset-x-0 w-full h-auto flex items-start justify-center py-4 aspect-square">
        <Image
          src={`${assetPrefix}/static/icons/ai-planner-page-2-bg.svg`}
          alt="planner eating goals background"
          fill
          className="object-contain"
          priority
        />
      </div>

      <ScrollArea className="flex-1 w-full max-h-[calc(100dvh-123px-88px-24px)] overflow-auto">
        {/* Title */}
        <div className="mb-6 mt-4">
          <h2 className="text-2xl font-bold text-[#3B82F6] leading-tight">
            Mục tiêu ăn uống
          </h2>
          <h2 className="text-2xl font-bold leading-tight">
            <span className="text-white bg-primary-40 rounded-full px-3 w-fit inline-block">
              của bạn trưa nay là gì?
            </span>
          </h2>
        </div>

        {/* Goals List */}
        <div className="flex flex-col gap-3 mb-6">
          {EATING_GOALS.map((goal) => (
            <button
              key={goal.value}
              onClick={() => handleGoalToggle(goal.value)}
              className={cn(
                'flex items-center gap-4 p-0 rounded-3xl transition-all text-left',
                selectedGoals.includes(goal.value)
                  ? 'ai-planner-button-active backdrop-blur-md bg-white/10 border border-[#ff8200]'
                  : 'ai-planner-button backdrop-blur-sm bg-white/5'
              )}
            >
              {/* Icon */}
              <div className="shrink-0 w-1/3 h-auto aspect-square rounded-l-3xl bg-white backdrop-blur-sm flex items-center justify-center">
                <Image
                  src={`${assetPrefix}/static/images/${goal.icon}`}
                  alt={goal.label}
                  width={80}
                  height={80}
                  className="object-contain"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3
                  className={cn(
                    'text-xl font-bold mb-1',
                    selectedGoals.includes(goal.value)
                      ? 'text-primary-40'
                      : 'text-white'
                  )}
                >
                  {goal.label}
                </h3>
                <p className="text-white/60 text-xs leading-relaxed">
                  {goal.description}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Spacer for bottom button */}
        <div className="h-24" />
      </ScrollArea>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className="glass-bubble rounded-full flex justify-between items-center px-4 py-2 h-16">
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                button_name: 'meal_plan_back',
                button_location: currentStep,
              });
              prevStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            ← Quay lại
          </Button>
          <StepIndicator />
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                event_name: 'click_button',
                button_name: 'meal_plan_step',
                button_location: currentStep,
              });
              nextStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            Tiếp tục →
          </Button>
        </div>
      </div>
    </div>
  );
}
